import { Link, useParams } from "react-router-dom";
import { useSeo } from "~/lib/seo/useSeo";
import { ASSUNTOS, acharAssunto } from "~/lib/ia/assuntos";
import type { Bloco } from "~/lib/ia/tipos";
import { PREVIA, CAPA } from "~/lib/videos";
import { EntreNaLista } from "~/ui/EntreNaLista";
import { NotFound } from "~/routes/NotFound";

/**
 * Uma página por assunto. É ela que o Google indexa e que continua
 * respondendo a pergunta depois que o reel sumiu do feed.
 *
 * A ordem da página responde na primeira tela e justifica depois: título,
 * resposta em duas frases, vídeo, explicação, fontes. Quem só queria saber
 * o que é RAG já foi embora satisfeito antes do terceiro parágrafo, e isso
 * é um bom resultado.
 */

function Corpo({ blocos }: { blocos: readonly Bloco[] }) {
  return (
    <>
      {blocos.map((b, i) => {
        if (b.tipo === "h2") {
          return (
            <h2
              key={i}
              className="mt-12 font-display text-[length:var(--text-step-1)] font-medium leading-tight"
            >
              {b.texto}
            </h2>
          );
        }
        if (b.tipo === "ul") {
          return (
            <ul key={i} className="mt-6 space-y-3 pl-5">
              {b.itens.map((item) => (
                <li key={item} className="list-disc leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="mt-6 leading-relaxed">
            {b.texto}
          </p>
        );
      })}
    </>
  );
}

export function IAAssunto() {
  const { assunto: slug } = useParams();
  const a = slug ? acharAssunto(slug) : undefined;

  if (!a) return <NotFound />;

  return <Pagina slug={a.slug} />;
}

function Pagina({ slug }: { slug: string }) {
  const a = acharAssunto(slug)!;
  useSeo({
    title: a.titulo,
    description: a.resumo,
    pathname: `/ia/${a.slug}`,
  });

  const outros = ASSUNTOS.filter((o) => o.slug !== a.slug).slice(0, 4);

  return (
    <main className="jg-shell py-12 sm:py-20">
      <p className="text-sm">
        <Link className="jg-link" to="/ia">
          Assuntos
        </Link>
      </p>

      <article className="mt-8 max-w-[62ch]">
        <h1 className="jg-display text-3xl text-balance sm:text-4xl">
          {a.titulo}
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-jg-fg">{a.abertura}</p>

        {/* O vídeo entra depois da resposta, não antes: quem chegou da busca
            quer o texto, e quem quer o vídeo o encontra do mesmo jeito. */}
        <figure className="jg-assunto-video mt-10">
          <video
            src={PREVIA(a.slug)}
            poster={CAPA(a.slug)}
            width={272}
            height={484}
            muted
            loop
            playsInline
            controls
            preload="none"
          />
          <figcaption className="mt-3 text-sm text-jg-fg-muted">
            Trecho do reel.{" "}
            <a
              className="jg-link"
              href="https://instagram.com/bittarg"
              rel="noreferrer noopener"
              target="_blank"
            >
              O vídeo inteiro está no @bittarg
            </a>
            .
          </figcaption>
        </figure>

        <div className="mt-4 text-jg-fg">
          <Corpo blocos={a.corpo} />
        </div>

        <section className="mt-16">
          <span className="jg-fio" />
          <h2 className="jg-eyebrow mt-6">Fontes</h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-jg-fg-muted">
            {a.fontes.map((f) => (
              <li key={f.nome}>
                {f.url ? (
                  <a
                    className="jg-link"
                    href={f.url}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    {f.nome}
                  </a>
                ) : (
                  f.nome
                )}
              </li>
            ))}
          </ul>
          {a.ressalva && (
            <p className="mt-4 text-sm leading-relaxed text-jg-fg-muted">
              {a.ressalva}
            </p>
          )}
          <p className="mt-4 text-sm text-jg-fg-muted">
            Conferido em{" "}
            <time dateTime={a.conferidoEm}>{dataLonga(a.conferidoEm)}</time>.
            Achou um erro?{" "}
            <a className="jg-link" href="mailto:joao@jgson.net">
              me escreva
            </a>{" "}
            e eu corrijo.
          </p>
        </section>
      </article>

      <section className="jg-grid mt-24 sm:mt-32">
        <span className="jg-fio col-span-full" />
        <div className="col-span-full pt-9 lg:col-span-6">
          <h2 className="font-display text-[length:var(--text-step-2)] font-medium leading-tight tracking-[-0.015em]">
            Receba o próximo por e-mail
          </h2>
          <p className="mt-6 max-w-[46ch] leading-relaxed text-jg-fg-muted">
            Um assunto por semana, com a explicação escrita e as fontes que eu
            conferi.
          </p>
        </div>
        <div className="col-span-full mt-10 lg:col-span-5 lg:col-start-8 lg:mt-1 lg:pt-9">
          <EntreNaLista origem={`ia-${a.slug}`} />
        </div>
      </section>

      <nav className="mt-24" aria-labelledby="h-outros">
        <h2 id="h-outros" className="jg-eyebrow">
          Outros assuntos
        </h2>
        <ul className="mt-5 space-y-2">
          {outros.map((o) => (
            <li key={o.slug}>
              <Link className="jg-link" to={`/ia/${o.slug}`}>
                {o.titulo}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </main>
  );
}

function dataLonga(iso: string): string {
  const [ano, mes, dia] = iso.split("-").map(Number);
  const meses = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];
  if (!ano || !mes || !dia) return iso;
  return `${dia} de ${meses[mes - 1]} de ${ano}`;
}
